/**
 * Field catalog + draft parsing for the `/pr/people/:id` inline editors
 * (2026-07-22).
 *
 * The SPEC TYPE and `parsePrEditDraft` are entity-agnostic and are reused by
 * `lib/inbox-matter-edit.ts` for the matter grid on `/inbox/threads/:id`; only
 * `PR_EDITABLE_FIELDS` below is PR-specific.
 *
 * The person card's header fields are editable in place, each dispatching a
 * one-field `update_outreach_target` — the SAME canonical action Peter uses,
 * so there is exactly one write path onto `outreach_targets` (invariant 5).
 * This module is the pure part: which fields are editable, how a raw input
 * draft becomes the wire value, and which fields may be cleared.
 *
 * Clearability follows the action's input schema, not taste: most string
 * fields carry `minLength: 1`, so an emptied draft CANNOT null them and the
 * editor refuses with a message instead of silently no-oping. Only the
 * maxLength-only prose fields (`suggested_angle`, `next_action`) accept an
 * empty save (stored as `''`, which the card renders as absent).
 */

export type PrEditFieldKind = "text" | "multiline" | "number" | "select";

/**
 * How a clearable field is EMPTIED on the wire — per action, not per taste.
 *
 * `update_outreach_target` clears with `''`. `update_inbox_thread` clears with
 * an explicit `null` and REJECTS `''` with `input_invalid` (its prose fields
 * carry `minLength: 1`, `urgency` is an enum). Getting this wrong is a visible
 * 400, not silent corruption — but it is still a clear that never lands.
 */
export type PrEditClearTo = "empty-string" | "null";

export interface PrEditFieldSpec {
  readonly field: string;
  readonly label: string;
  readonly kind: PrEditFieldKind;
  /** Empty draft is a valid save (schema has no minLength). */
  readonly clearable: boolean;
  /** Wire shape of a clear. Defaults to `''` so PR behaviour is unchanged. */
  readonly clearTo?: PrEditClearTo;
  /** Strip a leading `@` before saving (Instagram handles are stored bare). */
  readonly stripAt?: boolean;
  readonly options?: ReadonlyArray<string>;
  readonly placeholder?: string;
  readonly min?: number;
  readonly max?: number;
}

/** Everything the card lets staff edit, in render order. */
export const PR_EDITABLE_FIELDS: ReadonlyArray<PrEditFieldSpec> = [
  { field: "name", label: "Name", kind: "text", clearable: false },
  {
    field: "type",
    label: "Type",
    kind: "select",
    clearable: false,
    options: ["journalist", "influencer", "local_partner"],
  },
  {
    field: "status",
    label: "Status",
    kind: "select",
    clearable: false,
    options: [
      "discovered",
      "qualified",
      "queued",
      "contacted",
      "responded",
      "dormant",
      "dropped",
      "unreachable",
    ],
  },
  { field: "occupation", label: "Occupation", kind: "text", clearable: false },
  { field: "company_outlet", label: "Outlet", kind: "text", clearable: false },
  { field: "location", label: "Location", kind: "text", clearable: false },
  { field: "email", label: "Email", kind: "text", clearable: false },
  {
    field: "phone",
    label: "Phone / WhatsApp",
    kind: "text",
    clearable: false,
    // The conversation thread + WhatsApp reply target match on the EXACT
    // stored string, so it must be E.164 like every other phone in Layer.
    placeholder: "+44 7…  (international format)",
  },
  {
    field: "instagram",
    label: "Instagram",
    kind: "text",
    clearable: false,
    stripAt: true,
    placeholder: "handle (without @)",
  },
  { field: "linkedin", label: "LinkedIn", kind: "text", clearable: false },
  {
    field: "best_contact_method",
    label: "Best via",
    kind: "text",
    clearable: false,
  },
  {
    field: "audience_size",
    label: "Audience",
    kind: "number",
    clearable: false,
    min: 0,
  },
  { field: "niche", label: "Niche", kind: "text", clearable: false },
  {
    field: "quality_score",
    label: "Quality (0–100)",
    kind: "number",
    clearable: false,
    min: 0,
    max: 100,
  },
  {
    field: "suggested_angle",
    label: "Angle",
    kind: "multiline",
    clearable: true,
  },
  { field: "next_action", label: "Next", kind: "multiline", clearable: true },
  {
    // Mario's standing comment on the person — Peter reads it on every
    // `list_outreach_targets` and treats it as an instruction (mig 132).
    field: "staff_notes",
    label: "Notes to Peter",
    kind: "multiline",
    clearable: true,
    placeholder: "e.g. met her at the studio opening — mention it; don't pitch before September",
  },
];

export type PrEditParse =
  | { readonly ok: true; readonly value: string | number | null }
  | { readonly ok: false; readonly message: string };

/**
 * Turn a raw editor draft into the wire value for `update_outreach_target`,
 * or a human-readable refusal. Never returns a value the action's schema
 * would reject for shape (emptiness / non-integer / range).
 */
export function parsePrEditDraft(
  spec: PrEditFieldSpec,
  raw: string,
): PrEditParse {
  let draft = raw.trim();
  if (spec.stripAt === true) draft = draft.replace(/^@+/, "").trim();

  if (draft === "") {
    if (spec.clearable) {
      return { ok: true, value: spec.clearTo === "null" ? null : "" };
    }
    return {
      ok: false,
      message: `${spec.label} can't be emptied — enter a value or Cancel.`,
    };
  }

  if (spec.kind === "number") {
    const n = Number(draft);
    if (!Number.isInteger(n)) {
      return { ok: false, message: `${spec.label} must be a whole number.` };
    }
    if (spec.min !== undefined && n < spec.min) {
      return { ok: false, message: `${spec.label} must be ≥ ${spec.min}.` };
    }
    if (spec.max !== undefined && n > spec.max) {
      return { ok: false, message: `${spec.label} must be ≤ ${spec.max}.` };
    }
    return { ok: true, value: n };
  }

  return { ok: true, value: draft };
}
